import {
  EngagementScore,
  EngagementScoreScope,
  EngagementScoreTerminationReason,
  LiveProgramMetadata,
} from '@quickplay/player-interface';

export type EngagementComponentName = 'coverage' | 'activeRatio' | 'unmutedActiveRatio' | 'visibilityRatio';

export interface EngagementScoreComponents {
  coverage?: number;
  activeRatio?: number;
  unmutedActiveRatio?: number;
  visibilityRatio?: number;
}

const COMPONENT_WEIGHTS: Readonly<Record<EngagementComponentName, number>> = Object.freeze({
  coverage: 0.5,
  activeRatio: 0.15,
  unmutedActiveRatio: 0.2,
  visibilityRatio: 0.1,
});

export function calculateEngagementScore(components: EngagementScoreComponents): number | undefined {
  const entries = Object.entries(COMPONENT_WEIGHTS) as [EngagementComponentName, number][];
  let weightedValueSum = 0;
  let weightSum = 0;

  entries.forEach(([componentName, weight]) => {
    const componentValue = clampUnitInterval(components[componentName]);
    if (componentValue === undefined) {
      return;
    }

    weightedValueSum += componentValue * weight;
    weightSum += weight;
  });

  if (weightSum <= 0) {
    return undefined;
  }

  return clampScore(Math.round((100 * weightedValueSum) / weightSum));
}

export function clampUnitInterval(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }

  if (value <= 0) {
    return 0;
  }

  if (value >= 1) {
    return 1;
  }

  return value;
}

export function clampScore(value: number): number {
  if (value <= 0) {
    return 0;
  }

  if (value >= 100) {
    return 100;
  }

  return value;
}

export function roundToTwoDecimalPlaces(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export interface EngagementScoreResultOptions {
  scope: EngagementScoreScope;
  score: number;
  coverage: number;
  activeRatio: number;
  watchedDuration: number;
  pauseDuration: number;
  eligibleDuration: number;
  terminationReason: EngagementScoreTerminationReason;
  unmutedActiveRatio?: number;
  mutedWatchDuration?: number;
  activePlaybackDuration?: number;
  program?: LiveProgramMetadata;
}

export function createEngagementScoreResult(options: EngagementScoreResultOptions): string {
  const {
    scope,
    score,
    coverage,
    activeRatio,
    watchedDuration,
    pauseDuration,
    eligibleDuration,
    terminationReason,
    program,
    unmutedActiveRatio,
    mutedWatchDuration,
    activePlaybackDuration,
  } = options;

  const result: EngagementScore = Object.freeze({
    modelVersion: '2',
    scope,
    score: roundToTwoDecimalPlaces(score),
    coverage: roundToTwoDecimalPlaces(coverage),
    activeRatio: roundToTwoDecimalPlaces(activeRatio),
    watchedDuration: roundToTwoDecimalPlaces(watchedDuration),
    pauseDuration: roundToTwoDecimalPlaces(pauseDuration),
    eligibleDuration: roundToTwoDecimalPlaces(eligibleDuration),
    terminationReason,
    ...(program ? { program } : {}),
    ...(unmutedActiveRatio !== undefined
      ? {
          unmutedActiveRatio: roundToTwoDecimalPlaces(unmutedActiveRatio),
          mutedWatchDuration:
            mutedWatchDuration === undefined ? undefined : roundToTwoDecimalPlaces(mutedWatchDuration),
          activePlaybackDuration:
            activePlaybackDuration === undefined ? undefined : roundToTwoDecimalPlaces(activePlaybackDuration),
        }
      : {}),
  });

  return formatEngagementScore(result);
}

export function formatEngagementScore(result: EngagementScore): string {
  return `ts=${result.score} | c=${result.coverage} | ar=${result.activeRatio} | wd=${result.watchedDuration} | mwd=${result.mutedWatchDuration} | umar=${result.unmutedActiveRatio} | pd=${result.pauseDuration} | tr=${result.terminationReason}`;
}
